import { map, includes } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { skip } from 'rxjs/operators';
import { Domain } from './domain';
import type { LanguageNames, ILanguageName } from './language-names';
import { Injectable } from 'angular-ts-decorators';

/**
 * Language service in v7. It hardcodes the available languages (nl-nl & en-gb)
 * and the default one (nl-nl).
 * @example
 * // config phase
 * @example
    <example module="rfx">
      <file name="index.html"></file>
      <file name="some.js">
        appModule.service('some', function(language) {
            language.set('en-gb');
          });
      </file>
      <file name="other.js">
        appModule.service('other', function(language) {
            language.current$.subscribe((languageId) => {
              console.log(languageId);
            });
          });
      </file>
    </example>
 */
@Injectable('language')
export class Language {
  private languages: string[];
  private defaultLanguage: string;
  private LANGUAGE_COOKIE: string;
  private cookieValue: string;
  private COOKIE_TIMEOUT_DAY: number;
  private initValue: string;
  private currentSubject: BehaviorSubject<string>;

  /**
   * Property with current language id
   */
  public current: string;
  /**
   * Stream that emits the current language on subscribe & then emits every update
   */
  // TODO GATE-272 unit tests
  public current$: Observable<string>;

  constructor(
    languageNames: LanguageNames,
    private ipCookie,
    private domain: Domain,
  ) {
    'ngInject';

    this.languages = map(languageNames, (entry: ILanguageName) => entry.code);
    this.defaultLanguage = this.languages[0];
    this.LANGUAGE_COOKIE = 'lang';
    this.cookieValue = this.ipCookie(this.LANGUAGE_COOKIE);
    this.COOKIE_TIMEOUT_DAY = 180;

    if (this.cookieValue) {
      if (!this.isValid(this.cookieValue)) {
        // TODO notify sentry?
        this.initValue = this.languages[0];
      } else {
        this.initValue = this.cookieValue;
      }
    } else if (this.defaultLanguage) {
      this.initValue = this.defaultLanguage;
    } else if (this.languages.length) {
      this.initValue = this.languages[0];
    }

    this.current = this.initValue;

    this.currentSubject = new BehaviorSubject(this.initValue);
    this.current$ = this.currentSubject.asObservable();
  }

  private isValid(languageId: string) {
    return includes(this.languages, languageId);
  }

  /**
   * Get list of all available languages
   * @return {array}
   * Array of 2 chars language ids
   */
  getAll(): string[] {
    return this.languages;
  }

  /**
   * @param {string} languageId
   * Language identifier
   * Set the current language to languageId, if the languageId is different
   * then the current one & on {@link sharedModule.languageNames the list of
   * available languages}
   * @return {array}
   * 2 chars language ids
   */
  set(languageId: string): string[] {
    if (!this.isValid(languageId)) {
      throw new Error(
        'languageId ' +
          languageId +
          " wasn't availabe among " +
          this.languages +
          '.',
      );
    }

    if (this.current === languageId) {
      return;
    }

    this.current = languageId;

    this.ipCookie(this.LANGUAGE_COOKIE, languageId, {
      domain: this.domain.getParent(),
      path: '/',
      expires: this.COOKIE_TIMEOUT_DAY,
    });

    // some of callbacks can redirect us from page, and breaks the script
    this.currentSubject.next(languageId);
  }

  /**
   * Register callback for language change notifiction
   * Note! we don't guaranty callback execution. Some module can refresh
   * the page in a callback that is executed before some other.
   * @param {function} callback
   * Callback function. It will be registered and executed in order when
   * other language is set. The callback will be called with new languageId
   * as the only argument.
   * @throws error when callback is not a function
   * @returns {Void}
   * Nothing
   */
  // TODO replace with stream
  observe(callback: (...args) => void): void {
    if (!angular.isFunction(callback)) {
      throw new Error('callback ' + callback + ' is not a function.');
    }

    this.currentSubject.pipe(skip(1)).subscribe(callback);
  }
}
